// The corner window: the hunt in a small always-on-top window to keep in a corner of the screen while working, the
// way Rusty's Retirement sits along the bottom of the desktop. Drag its edge to make it a low strip along the
// bottom, a card in a corner or a column down the side; the layout follows the shape.
//
// How it opens: Document Picture-in-Picture where the browser has it (Chrome and Edge 116+, Firefox 151+), which
// floats above every other window and closes with the game's tab; elsewhere (Safari) a small popup window, which
// stays on top only if the system pins it. Either way the window is a blank page of this origin that the game's
// own code fills and drives: one connection, one copy of the state, nothing loaded twice. It runs on its own
// animation frames and timers, so it keeps moving while the game's tab is in the background.
//
// The game calls three things (see docs/corner-window.md): toggleCorner() from a click or a key (browsers only
// open these windows from a user's action), cornerEvents(evs) with every batch of hunt events, and optionally
// onCornerChange() to show the button pressed while the window is open.
import type { GameEvent } from '../../shared/protocol.ts';
import { S } from '../state.ts';
import { go } from '../router.ts';
import { CornerScene } from './scene.ts';
import { CornerHud } from './hud.ts';
import { chime, closeChime } from './chime.ts';
import { momentsFrom, presetSize, sanitizeSize, saverSize, ZOOM_MIN, ZOOM_MAX, type Shape } from './model.ts';
import { opensBetween } from '../hud.ts';
import { claimGolden, goldenName, goldenNow, onGoldenChange } from '../golden.ts';

/** How the window opened: Picture-in-Picture (always on top), or a popup. */
export type CornerKind = 'pip' | 'popup';
/** What opening did; 'blocked' means the browser refused a window (popups blocked). */
export type CornerResult = CornerKind | 'blocked';

interface Open { win: Window; kind: CornerKind; scene: CornerScene; hud: CornerHud; raf: number; timers: number[]; watch: number; frames: number }
let cw: Open | null = null;
/** Why the last open fell back to a popup, for the browser checks and the console. */
let pipRefused = '';
let opening: Promise<CornerResult> | null = null;
const listeners = new Set<(open: boolean) => void>();

// Remembered per browser: the window's size (one with the scene, one in battery saver), a zoom step per layout,
// battery saver, and whether Auras chime.
const SIZE_KEY = 'mi-corner-size', SAVER_SIZE_KEY = 'mi-corner-size-saver', ZOOM_KEY = 'mi-corner-zoom', SOUND_KEY = 'mi-corner-sound', SAVER_KEY = 'mi-corner-saver';
function load<T>(key: string, fallback: T): T { try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch { return fallback; } }
function save(key: string, v: unknown) { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* storage off */ } }
let zooms: Record<Shape, number> = { strip: 0, card: 0, column: 0, ...load<Partial<Record<Shape, number>>>(ZOOM_KEY, {}) };

/**
 * Whether this device can keep a corner window: a desktop pointer, and Picture-in-Picture or a real popup window
 * (a phone opens a popup as just another tab). Hide the button where it is false.
 */
export function cornerSupported(): boolean {
  const fine = typeof matchMedia === 'function' && matchMedia('(pointer: fine)').matches;
  return fine && ('documentPictureInPicture' in window || !/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent));
}

/** True while the corner window is open. */
export const cornerOpen = () => !!cw;
/** True while the open window chimes for Auras itself (its bell is on). */
export const cornerAuraChime = () => !!cw?.hud.soundOn;
/** How the open window was made, or null. */
export const cornerKind = (): CornerKind | null => cw?.kind ?? null;

/** Called with true when the window opens and false when it closes (from anywhere, its own close button too). */
export function onCornerChange(fn: (open: boolean) => void) { listeners.add(fn); return () => listeners.delete(fn); }
const changed = () => { for (const fn of listeners) { try { fn(!!cw); } catch { /* a listener's own problem */ } } };

/** Opens the window, or closes it when it is open. Call from a click or key handler. */
export function toggleCorner(): Promise<CornerResult | 'closed'> {
  if (cw) { closeCorner(); return Promise.resolve('closed'); }
  return openCorner();
}

/** Opens the corner window (or keeps the open one). Call from a click or key handler. */
export function openCorner(): Promise<CornerResult> {
  if (cw) return Promise.resolve(cw.kind);
  opening ??= (async (): Promise<CornerResult> => {
    const saver = load<boolean>(SAVER_KEY, false) === true;
    const size = sanitizeSize(load(saver ? SAVER_SIZE_KEY : SIZE_KEY, null) ?? (saver ? load(SIZE_KEY, null) : null), screen.availWidth, screen.availHeight);
    let win: Window | null = null, kind: CornerKind = 'pip';
    const pip = (window as unknown as { documentPictureInPicture?: { requestWindow(o: { width: number; height: number }): Promise<Window> } }).documentPictureInPicture;
    pipRefused = pip ? '' : 'no Document Picture-in-Picture in this browser';
    if (pip) {
      try { win = await pip.requestWindow({ width: size.w, height: size.h }); } catch (e) { win = null; pipRefused = String((e as Error)?.message ?? e); }
    }
    if (!win) {
      console.info(`Corner window: opening a popup (${pipRefused})`);
      kind = 'popup';
      const scr = screen as Screen & { availLeft?: number; availTop?: number };
      const left = Math.max(0, (scr.availLeft ?? 0) + screen.availWidth - size.w - 24);
      const top = Math.max(0, (scr.availTop ?? 0) + screen.availHeight - size.h - 72);
      win = window.open('', 'legendkin-corner', `popup,width=${size.w},height=${size.h},left=${left},top=${top}`);
      if (!win) return 'blocked';
    }
    mount(win, kind);
    return kind;
  })().finally(() => { opening = null; });
  return opening;
}

/** Closes the corner window if it is open. */
export function closeCorner() {
  const w = cw?.win;
  teardown();
  try { w?.close(); } catch { /* already closed */ }
}

function teardown() {
  if (!cw) return;
  const { win, raf, timers, watch } = cw;
  cw = null;
  window.clearInterval(watch);
  try { win.cancelAnimationFrame(raf); for (const t of timers) win.clearInterval(t); } catch { /* window gone */ }
  closeChime();
  changed();
}

/** Every batch of hunt events, after the game has applied it (client/main.ts applyEvents). */
export function cornerEvents(evs: GameEvent[]) {
  if (!cw) return;
  // Effects only age on drawn frames; a hidden window, battery saver or the open drop-down queue none.
  if (cw.win.document.visibilityState === 'visible' && cw.hud.sceneShown) cw.scene.events(evs);
  const wildOf = (uid: number) => S.field?.wilds.find((w) => w.uid === uid) ?? S.prevField?.wilds.find((w) => w.uid === uid);
  const list = momentsFrom(evs, { caught: (sp) => S.trainer?.dex[sp]?.caught ?? 0, wild: wildOf, opens: opensBetween });
  if (!list.length) return;
  cw.hud.moments(list);
  // A chime is the point of sound while working, so it plays even while the window is covered.
  const loud = list.find((m) => m.chime === 'aura-caught') ?? list.find((m) => m.chime === 'aura-seen');
  if (loud?.chime && cw.hud.soundOn) chime(cw.win, loud.chime);
}

/** Copies the game's stylesheets and fonts into the window and lays our own (public/corner.css) over them. */
function dress(win: Window) {
  const doc = win.document;
  doc.head.replaceChildren();
  doc.body.replaceChildren();
  const base = doc.createElement('base');
  base.href = new URL('./', location.href).href;
  doc.head.append(base);
  const meta = doc.createElement('meta');
  meta.setAttribute('charset', 'utf-8');
  doc.head.append(meta);
  doc.title = 'Legendkin';
  for (const l of document.querySelectorAll<HTMLLinkElement>('link[rel~="stylesheet"], link[rel~="icon"], link[rel="preconnect"]')) {
    if (l.disabled) continue;
    const c = doc.createElement('link');
    c.rel = l.rel; c.href = l.href;
    if (l.crossOrigin !== null) c.crossOrigin = l.crossOrigin;
    if (l.sizes?.value) c.setAttribute('sizes', l.sizes.value);
    doc.head.append(c);
  }
  // Our own sheet goes last; the body stays hidden until it has loaded, so the window never flashes unstyled.
  const own = doc.createElement('link');
  own.rel = 'stylesheet';
  own.href = 'corner.css';
  doc.body.style.visibility = 'hidden';
  let shown = false;
  const show = () => { if (shown) return; shown = true; doc.body.style.visibility = ''; win.dispatchEvent(new (win as Window & typeof globalThis).Event('resize')); };
  own.addEventListener('load', show);
  own.addEventListener('error', show);
  win.setTimeout(show, 1500);
  doc.head.append(own);
  const root = document.documentElement, croot = doc.documentElement;
  croot.lang = root.lang || 'en';
  if (root.dataset.theme) croot.dataset.theme = root.dataset.theme; else delete croot.dataset.theme;
  if (root.dataset.region) croot.dataset.region = root.dataset.region;
}

function mount(win: Window, kind: CornerKind) {
  dress(win);
  const doc = win.document;
  const root = doc.createElement('div');
  const canvas = doc.createElement('canvas');
  canvas.className = 'cv-scene';
  canvas.setAttribute('aria-label', 'Your hunt');
  root.append(canvas);
  doc.body.append(root);

  const scene = new CornerScene(canvas, win);
  // Sizes are inner sizes; the window adds its own frame. A window that would grow past the screen's right or bottom
  // edge moves back in first (a popup can; Picture-in-Picture keeps its place). A browser may refuse either, and the
  // layout follows whatever size it got.
  const resize = (p: { w: number; h: number }) => {
    const scr = screen as Screen & { availLeft?: number; availTop?: number };
    const W = p.w + (win.outerWidth - win.innerWidth), H = p.h + (win.outerHeight - win.innerHeight);
    const over = (pos: number, size: number, start: number, room: number) => Math.max(0, pos + size - (start + room));
    const dx = over(win.screenX, W, scr.availLeft ?? 0, screen.availWidth), dy = over(win.screenY, H, scr.availTop ?? 0, screen.availHeight);
    try { if (dx || dy) win.moveBy(-dx, -dy); } catch { /* not allowed here */ }
    try { win.resizeTo(W, H); } catch { /* not allowed here */ }
  };
  const hud = new CornerHud(root, win, {
    shape: (s) => { hud.setSaver(false); save(SAVER_KEY, false); resize(presetSize(s, screen.availWidth, screen.availHeight)); relayout(); },
    zoom: (dir) => {
      const s = hud.shape;
      zooms = { ...zooms, [s]: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zooms[s] + dir)) };
      scene.zoom = zooms[s];
      save(ZOOM_KEY, zooms);
    },
    // Battery saver drops the scene, so the window takes the size it had in battery saver last time (a strip
    // keeps its own); turning it off brings back the size it had with the scene.
    saver: () => {
      const on = !hud.saver, cur = { w: win.innerWidth, h: win.innerHeight }, strip = hud.shape === 'strip';
      save(on ? SIZE_KEY : SAVER_SIZE_KEY, cur);
      hud.setSaver(on);
      save(SAVER_KEY, on);
      if (!strip) {
        const back = load(on ? SAVER_SIZE_KEY : SIZE_KEY, null);
        const next = back ? sanitizeSize(back, screen.availWidth, screen.availHeight) : on ? saverSize(cur, hud.shape, screen.availHeight) : cur;
        if (next.w !== cur.w || next.h !== cur.h) resize(next);
      }
      relayout();
    },
    sound: () => {
      hud.setSound(!hud.soundOn);
      save(SOUND_KEY, hud.soundOn);
      if (hud.soundOn) chime(win, 'aura-seen');
    },
    game: (hash) => {
      if (hash) go(hash);
      try { window.focus(); } catch { /* the browser decides */ }
    },
    claim: () => claimGolden(),
  });
  hud.setSound(load<boolean>(SOUND_KEY, false) === true);
  hud.setSaver(load<boolean>(SAVER_KEY, false) === true);
  const quietQuery = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
  scene.quiet = !!quietQuery?.matches;

  // The HUD picks the layout first: it decides how much of the window the scene gets.
  const relayout = () => {
    hud.layout();
    scene.shape = hud.shape;
    scene.zoom = zooms[hud.shape];
    scene.fit();
  };
  let saveAt = 0;
  win.addEventListener('resize', () => {
    relayout();
    win.clearTimeout(saveAt);
    saveAt = win.setTimeout(() => save(hud.saver && hud.shape !== 'strip' ? SAVER_SIZE_KEY : SIZE_KEY, { w: win.innerWidth, h: win.innerHeight }), 500);
  });
  canvas.addEventListener('wheel', (e) => { e.preventDefault(); const s = hud.shape; zooms = { ...zooms, [s]: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zooms[s] + (e.deltaY < 0 ? 1 : -1))) }; scene.zoom = zooms[s]; save(ZOOM_KEY, zooms); }, { passive: false });
  win.addEventListener('pagehide', () => { if (cw?.win === win) teardown(); });

  const open: Open = { win, kind, scene, hud, raf: 0, timers: [], watch: 0, frames: 0 };
  cw = open;
  relayout();
  void doc.fonts?.ready.then(() => { if (cw === open) relayout(); });

  // About 30 frames a second is plenty for pixel art and light on a window that stays open all day. With the scene
  // out of sight (battery saver, the drop-down) only the banners need a frame now and then.
  let last = -1e9;
  const frame = (t: number) => {
    if (cw !== open) return;
    open.raf = win.requestAnimationFrame(frame);
    const drawn = hud.sceneShown;
    if (t - last < (drawn ? 30 : 200)) return;
    last = t;
    open.frames++;
    if (drawn) scene.draw(t);
    hud.frame(performance.now());
  };
  open.raf = win.requestAnimationFrame(frame);
  open.timers.push(win.setInterval(() => { if (cw === open) relayout(); }, 500));
  // A window closed some other way than pagehide is noticed here, on the game's own clock (its clock stops with it).
  open.watch = window.setInterval(() => { if (cw === open && win.closed) teardown(); }, 2000);
  changed();
}

// A Golden Wild shows on the window's scene too, with a banner when it arrives.
let goldenSeen = 0;
onGoldenChange(() => {
  const g = goldenNow();
  if (!cw) return;
  if (g && g.id !== goldenSeen) cw.hud.moments([{ text: `${goldenName(g.species)} · click it`, pri: 5, tone: 'gold', ms: 3600 }]);
  goldenSeen = g?.id ?? goldenSeen;
  cw.hud.golden();
});

// The window can't outlive the game: a popup is closed with it (Picture-in-Picture closes by itself).
window.addEventListener('pagehide', () => closeCorner());

// Read by the browser checks.
(globalThis as { miCorner?: () => unknown }).miCorner = () => cw && { kind: cw.kind, shape: cw.hud.shape, saver: cw.hud.saver, open: cw.hud.open, fx: cw.scene.effects, frames: cw.frames, w: cw.win.innerWidth, h: cw.win.innerHeight, ...(pipRefused ? { pipRefused } : {}) };
